import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { createHealthMapReport, updateHealthMapReport, deleteHealthMapReport } from "../../../redux/Thunk/HealthMap/HealthMapThunks";
import { setActiveOverlay } from "../../../redux/Reducer/HealthMap/HealthMapSlice";

const HealthMapReportEditor = ({ record, PID, isNew, onDone, isOwner: isOwnerProp, onFileClick }) => {
    const dispatch = useDispatch();
    const [title, setTitle] = useState(record?.data?.title || "");
    const [description, setDescription] = useState(record?.data?.description || "");
    const [isEditing, setIsEditing] = useState(isNew || false);
    const [saving, setSaving] = useState(false);
    const [visibility] = useState(record?.data?.visibility || "private");
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState(record?.data?.fileName || null);
    const fileInputRef = React.useRef(null);
    
    const isOwner = isNew || isOwnerProp;

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setFileName(selectedFile.name);
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        setFileName(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSave = async (v) => {
        if (!title.trim() || (!file && !record?.data?.fileUrl)) return;
        const finalVisibility = v || visibility;
        setSaving(true);
        
        const formData = new FormData();
        formData.append("PID", PID);
        formData.append("title", title.trim());
        formData.append("description", description.trim());
        formData.append("visibility", finalVisibility);
        if (file) {
            formData.append("file", file);
        }

        try {
            if (isNew) {
                await dispatch(createHealthMapReport(formData));
                dispatch(setActiveOverlay(null));
                if (onDone) onDone();
            } else {
                await dispatch(updateHealthMapReport({ id: record.id, formData }));
                setIsEditing(false);
                if (onDone) onDone();
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (isNew) {
            dispatch(setActiveOverlay(null));
            if (onDone) onDone();
        } else {
            setTitle(record?.data?.title || "");
            setDescription(record?.data?.description || "");
            setFile(null);
            setFileName(record?.data?.fileName || null);
            setIsEditing(false);
        }
    };

    const getFileIcon = (type) => {
        if (type?.includes("pdf")) return "📄";
        if (type?.includes("word") || type?.includes("officedocument.wordprocessingml")) return "📝";
        if (type?.includes("excel") || type?.includes("spreadsheetml") || type?.includes("csv")) return "📊";
        return "📁";
    };

    // Read-only view for saved records
    if (!isEditing && !isNew) {
        return (
            <div>
                <div className="hm-card-header" style={{ marginBottom: 12 }}>
                    <span className="hm-card-type report">📁 Clinical Report</span>
                    {record.updatedAt !== record.createdAt && (
                        <span className="hm-card-id">edited</span>
                    )}
                </div>
                
                <div 
                    className="hm-report-file-box" 
                    onClick={() => onFileClick ? onFileClick(record.data.fileUrl, record.data.title) : window.open(record.data.fileUrl, "_blank")}
                >
                    <div className="file-icon">{getFileIcon(record.data.fileType)}</div>
                    <div className="file-info">
                        <div className="file-name">{record.data.title}</div>
                        <div className="file-meta">{record.data.fileName || "View Report"}</div>
                    </div>
                    <div className="file-download-arrow">→</div>
                </div>

                {record?.data?.description && (
                    <div className="hm-card-body" style={{ marginTop: 12, fontSize: 13, color: "var(--hm-text-secondary)" }}>
                        {record.data.description}
                    </div>
                )}

                <div className="hm-card-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
                    <span className={`hm-visibility-badge ${record?.data?.visibility}`}>
                        {record?.data?.visibility === "collaborative" ? "🌐 Collaborative Report" : "🔒 Private Report"}
                    </span>
                    {isOwner && (
                        <div style={{ display: "flex", gap: 8 }}>
                            <button
                                className="hm-edit-prominent-btn"
                                onClick={() => {
                                    if (window.confirm("Are you sure you want to delete this report? This action cannot be undone.")) {
                                        dispatch(deleteHealthMapReport(record.id));
                                    }
                                }}
                                style={{ background: "rgba(255, 0, 0, 0.05)", color: "#ff4d4f", border: "1px solid rgba(255, 0, 0, 0.1)" }}
                            >
                                🗑️ Delete
                            </button>
                            <button
                                className="hm-edit-prominent-btn"
                                onClick={() => setIsEditing(true)}
                            >
                                ✏️ Edit
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit / Create view
    return (
        <div className="record-editor-inner">
            <input
                type="text"
                className="record-title-input"
                placeholder="Report Title (e.g. Blood Test, MRI Scan)..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid var(--hm-border)",
                    background: "var(--hm-surface-hover)",
                    color: "var(--hm-text)",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "12px",
                    outline: "none"
                }}
            />
            
            <textarea
                className="record-textarea"
                placeholder="Description or notes about this report (optional)..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ minHeight: "80px", marginBottom: "12px" }}
            />
            
            <div className="record-image-upload-zone" style={{ borderStyle: "dashed" }}>
                {fileName ? (
                    <div className="record-file-preview-container" style={{ position: "relative", width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px", background: "var(--hm-bg)", borderRadius: "8px", border: "1px solid var(--hm-border)" }}>
                        <div style={{ fontSize: "28px" }}>{getFileIcon(file?.type || record?.data?.fileType)}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--hm-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {fileName}
                            </div>
                            <div style={{ fontSize: "11px", color: "var(--hm-text-muted)" }}>
                                {file ? "Staged for upload" : "Existing file"}
                            </div>
                        </div>
                        <button 
                            className="remove-preview-btn" 
                            onClick={handleRemoveFile}
                            title="Remove file"
                        >
                            ✕
                        </button>
                    </div>
                ) : (
                    <div className="upload-placeholder" onClick={() => fileInputRef.current.click()}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="12" y1="18" x2="12" y2="12"/>
                            <line x1="9" y1="15" x2="15" y2="15"/>
                        </svg>
                        <span>Click to Upload Report (PDF, Word, Excel, etc.)</span>
                    </div>
                )}
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    style={{ display: "none" }} 
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" 
                    onChange={handleFileChange}
                />
            </div>

            <div className="record-actions" style={{ marginTop: "16px" }}>
                <button className="record-cancel-btn" onClick={handleCancel}>Cancel</button>
                <div style={{ display: "flex", gap: 8 }}>
                    {isNew ? (
                        <>
                            <button
                                className="record-save-btn private"
                                onClick={() => handleSave("private")}
                                disabled={saving || !title.trim() || !file}
                            >
                                {saving ? "Saving..." : "Save Private Report"}
                            </button>
                            <button
                                className="record-save-btn collaborative"
                                onClick={() => handleSave("collaborative")}
                                disabled={saving || !title.trim() || !file}
                            >
                                {saving ? "Saving..." : "Save Collaborative Report"}
                            </button>
                        </>
                    ) : (
                        <button
                            className="record-save-btn"
                            onClick={() => handleSave()}
                            disabled={saving || !title.trim()}
                        >
                            {saving ? "Saving..." : "Save Changes"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HealthMapReportEditor;
